import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Card,
  Row,
  Col,
  InputNumber,
  Button,
  Table,
  Select,
  Typography,
  Tabs,
  Image,
  Radio,
  Input,
  message,
} from "antd";

const { Title } = Typography;
const { Option } = Select;

function LinearProgramming({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) {
  const token = localStorage.getItem("access_token");

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const [title, setTitle] = useState(fileName || "Linear Programming");
  const [problemType, setProblemType] = useState(initialConfig?.objective || "max");
  const [variableCount, setVariableCount] = useState(initialConfig?.variables || 2);
  const [constraintCount, setConstraintCount] = useState(initialConfig?.constraints || 2);
  const [variableNaming, setVariableNaming] = useState("X");

  const [objectiveCoeffs, setObjectiveCoeffs] = useState(
    Array(initialConfig?.variables || 2).fill(0)
  );

  const [constraints, setConstraints] = useState(
    Array.from({ length: initialConfig?.constraints || 2 }, () => ({
      coeffs: Array(initialConfig?.variables || 2).fill(0),
      type: "<=",
      rhs: 0,
    }))
  );

  const [result, setResult] = useState(null);

  const getVariableName = (index) => {
    if (variableNaming === "X") return `X${index + 1}`;
    if (variableNaming === "lower") return String.fromCharCode(97 + index);
    if (variableNaming === "upper") return String.fromCharCode(65 + index);
    if (variableNaming === "number") return `${index + 1}`;
    return `X${index + 1}`;
  };

  useEffect(() => {
    if (fileId) {
      retrieveFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const retrieveFile = async (savedId) => {
    try {
      const response = await axios.get(
        `/api/retrieve_linear_programming/${savedId || fileId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const file = response.data;
      const input = file.input_data;

      setTitle(file.name || "Linear Programming");
      setProblemType(input.problem_type);
      setObjectiveCoeffs(input.objective_coeffs);
      setVariableCount(input.objective_coeffs.length);

      const rows = input.constraint_coeffs.map((row, index) => ({
        coeffs: row,
        type: input.constraint_types[index],
        rhs: input.rhs[index],
      }));

      setConstraints(rows);
      setConstraintCount(rows.length);
      setResult(file.output_data);
      setActiveTab("2");
    } catch (error) {
      console.log("RETRIEVE ERROR", error);
      message.error("Failed to load saved problem");
    }
  };

  const buildInputData = () => {
    return {
      problem_type: problemType,
      objective_coeffs: objectiveCoeffs.map(Number),
      constraint_coeffs: constraints.map((c) => c.coeffs.map(Number)),
      constraint_types: constraints.map((c) => c.type),
      rhs: constraints.map((c) => Number(c.rhs)),
    };
  };

  const handleCalculate = async () => {
    setLoading(true);

    try {
      const payload = {
        name: title,
        input_data: buildInputData(),
      };

      let response;

      if (fileId) {
        response = await axios.put(
          `/api/update_linear_programming/${fileId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          "/api/save_linear_programming/",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      console.log("LP RESPONSE", JSON.stringify(response.data, null, 2));

      if (response.data.id) {
        // Make sure the parent knows about the (possibly new) file id,
        // otherwise the next "Solve" click will create a duplicate record.
        if (typeof setSelectedFile === "function") {
          setSelectedFile((prev) => ({
            ...(typeof prev === "object" && prev ? prev : {}),
            id: response.data.id,
            name: title,
            type: "linear_programming",
          }));
        }
        await retrieveFile(response.data.id);
      } else {
        setResult(
          response.data.output_data || response.data.data || response.data
        );
      }

      message.success("Calculation Successful");
      setActiveTab("2");
    } catch (error) {
      console.log(error.response?.data || error);
      message.error("Calculation Failed");
    }

    setLoading(false);
  };

  const updateObjective = (index, value) => {
    const temp = [...objectiveCoeffs];
    temp[index] = value || 0;
    setObjectiveCoeffs(temp);
  };

  const updateConstraintCoeff = (row, col, value) => {
    const temp = constraints.map((c, i) =>
      i === row
        ? { ...c, coeffs: c.coeffs.map((v, j) => (j === col ? value || 0 : v)) }
        : c
    );
    setConstraints(temp);
  };

  const updateConstraintType = (row, value) => {
    const temp = constraints.map((c, i) =>
      i === row ? { ...c, type: value } : c
    );
    setConstraints(temp);
  };

  const updateConstraintRhs = (row, value) => {
    const temp = constraints.map((c, i) =>
      i === row ? { ...c, rhs: value || 0 } : c
    );
    setConstraints(temp);
  };

  const changeVariableCount = (count) => {
    if (!count || count < 1) return;

    setVariableCount(count);

    setObjectiveCoeffs((prev) =>
      Array.from({ length: count }, (_, i) => prev[i] ?? 0)
    );

    setConstraints((prev) =>
      prev.map((c) => ({
        ...c,
        coeffs: Array.from({ length: count }, (_, i) => c.coeffs[i] ?? 0),
      }))
    );
  };

  const changeConstraintCount = (count) => {
    if (!count || count < 1) return;

    const rows = Array.from(
      { length: count },
      (_, i) =>
        constraints[i] || {
          coeffs: Array(variableCount).fill(0),
          type: "<=",
          rhs: 0,
        }
    );

    setConstraints(rows);
    setConstraintCount(count);
  };

  const addConstraint = () => {
    setConstraints([
      ...constraints,
      {
        coeffs: Array(variableCount).fill(0),
        type: "<=",
        rhs: 0,
      },
    ]);

    setConstraintCount(constraints.length + 1);
  };

  const removeConstraint = (index) => {
    if (constraints.length <= 1) return;

    setConstraints(constraints.filter((_, i) => i !== index));
    setConstraintCount(constraints.length - 1);
  };

  const tableData = [
    {
      key: "objective",
      name: problemType === "max" ? "Maximize" : "Minimize",
      ...objectiveCoeffs.reduce((obj, val, index) => {
        obj[getVariableName(index)] = val;
        return obj;
      }, {}),
    },

    ...constraints.map((c, index) => ({
      key: index,
      name: `Constraint ${index + 1}`,
      ...c.coeffs.reduce((obj, val, col) => {
        obj[getVariableName(col)] = val;
        return obj;
      }, {}),
      type: c.type,
      rhs: c.rhs,
    })),
  ];

  return (
    <Card>
      <Title level={3}>{title}</Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "1",
            label: "Input",
            children: (
              <>
                <Card title="Problem Setup">
                  <Row gutter={16}>
                    <Col span={6}>
                      <Input
                        placeholder="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </Col>

                    <Col span={6}>
                      <InputNumber
                        min={1}
                        style={{ width: "100%" }}
                        value={variableCount}
                        onChange={changeVariableCount}
                      />
                    </Col>

                    <Col span={6}>
                      <InputNumber
                        min={1}
                        style={{ width: "100%" }}
                        value={constraintCount}
                        onChange={changeConstraintCount}
                      />
                    </Col>
                  </Row>

                  <br />

                  <h4>Objective</h4>
                  <Radio.Group
                    value={problemType}
                    onChange={(e) => setProblemType(e.target.value)}
                  >
                    <Radio value="max">Maximize</Radio>
                    <Radio value="min">Minimize</Radio>
                  </Radio.Group>

                  <br />
                  <br />

                  <h4>Variable Names</h4>
                  <Radio.Group
                    value={variableNaming}
                    onChange={(e) => setVariableNaming(e.target.value)}
                  >
                    <Radio value="X">X1,X2,X3</Radio>
                    <Radio value="lower">a,b,c</Radio>
                    <Radio value="upper">A,B,C</Radio>
                    <Radio value="number">1,2,3</Radio>
                  </Radio.Group>
                </Card>

                <br />

                <Card title="Linear Programming Model">
                  <Table
                    bordered
                    pagination={false}
                    dataSource={tableData}
                    columns={[
                      { title: "", dataIndex: "name" },

                      ...Array.from({ length: variableCount }, (_, index) => ({
                        title: getVariableName(index),
                        dataIndex: getVariableName(index),
                        render: (_, record) => {
                          if (record.key === "objective") {
                            return (
                              <InputNumber
                                value={objectiveCoeffs[index]}
                                onChange={(v) => updateObjective(index, v)}
                              />
                            );
                          }

                          return (
                            <InputNumber
                              value={constraints[record.key]?.coeffs[index]}
                              onChange={(v) =>
                                updateConstraintCoeff(record.key, index, v)
                              }
                            />
                          );
                        },
                      })),

                      {
                        title: "",
                        dataIndex: "type",
                        width: 70,
                        render: (value, record) => {
                          if (record.key === "objective") return null;

                          return (
                            <Select
                              value={value}
                              style={{ width: "100%" }}
                              onChange={(v) => updateConstraintType(record.key, v)}
                            >
                              <Option value="<=">≤</Option>
                              <Option value=">=">≥</Option>
                              <Option value="=">=</Option>
                            </Select>
                          );
                        },
                      },

                      {
                        title: "RHS",
                        dataIndex: "rhs",
                        render: (value, record) => {
                          if (record.key === "objective") return null;

                          return (
                            <InputNumber
                              value={value}
                              onChange={(v) => updateConstraintRhs(record.key, v)}
                            />
                          );
                        },
                      },

                      {
                        title: "Equation form",
                        dataIndex: "equationForm",
                        align: "right",
                        render: (_, record) => {
                          if (record.key === "objective") {
                            return problemType === "max" ? "Max" : "Min";
                          }

                          const symbol =
                            record.type === "<="
                              ? "≤"
                              : record.type === ">="
                                ? "≥"
                                : "=";

                          return `${symbol} ${record.rhs}`;
                        },
                      },

                      {
                        title: "",
                        dataIndex: "actions",
                        render: (_, record) => {
                          if (record.key === "objective") return null;

                          return (
                            <Button
                              danger
                              size="small"
                              disabled={constraints.length <= 1}
                              onClick={() => removeConstraint(record.key)}
                            >
                              Remove
                            </Button>
                          );
                        },
                      },
                    ]}
                  />
                </Card>

                <br />

                <Button onClick={addConstraint}>Add Constraint</Button>

                <br />
                <br />

                <Button type="primary" loading={loading} onClick={handleCalculate}>
                  Solve
                </Button>
              </>
            ),
          },

          {
            key: "2",
            label: "Results",
            children: result ? (
              <>
                <Card title="Optimal Solution">
                  <p>
                    <b>Objective Value:</b> {result.objective_value}
                  </p>

                  {(result.variables || result.solution)?.map((v, index) => (
                    <p key={index}>
                      <b>{getVariableName(index)}:</b> {v}
                    </p>
                  ))}
                </Card>

                <Card title="Basis">
                  <p>{result.basis?.join(", ")}</p>
                </Card>

                <Card title="Sensitivity Analysis">
                  <Table
                    bordered
                    pagination={false}
                    dataSource={[
                      {
                        key: "Reduced Costs",
                        value: result.sensitivity_analysis?.reduced_costs?.join(", "),
                      },
                      {
                        key: "Shadow Prices",
                        value: result.sensitivity_analysis?.shadow_prices?.join(", "),
                      },
                      {
                        key: "Slack / Surplus",
                        value: result.sensitivity_analysis?.slack_surplus?.join(", "),
                      },
                    ]}
                    columns={[
                      { title: "Statistic", dataIndex: "key" },
                      { title: "Value", dataIndex: "value" },
                    ]}
                  />
                </Card>

                {result.final_tableau?.length > 0 && (
                  <Card title="Final Tableau">
                    <Table
                      bordered
                      pagination={false}
                      dataSource={result.final_tableau.map((row, index) => ({
                        key: index,
                        ...row.reduce((obj, value, i) => {
                          obj[`C${i + 1}`] = value;
                          return obj;
                        }, {}),
                      }))}
                      columns={result.final_tableau[0].map((_, index) => ({
                        title: `C${index + 1}`,
                        dataIndex: `C${index + 1}`,
                      }))}
                    />
                  </Card>
                )}

                {result.chart_url && (
                  <Card title="Graph">
                    <Image src={result.chart_url} width={600} />
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <Title level={5}>No results available.</Title>
                <p>
                  Enter values and click <b>Solve</b>.
                </p>
              </Card>
            ),
          },
        ]}
      />
    </Card>
  );
}

export default LinearProgramming;
